import { useMemo } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { useSelector } from 'react-redux';

import Global from '../../../store/global-selectors';
import { convertDateToMomentDateObjAtMidnightInSellerTimeZone } from '../../../services/datetime-service';
import { SHIPPY_FILTERS_STATE_KEY, FILTER_IDS } from '../constants/shippy-search-constants';

export type ShippyFilter = {
  id: string;
  value: unknown;
  operator?: unknown;
};

type DateValue = {
  from?: string;
  to?: string;
};

type LocationState = {
  [SHIPPY_FILTERS_STATE_KEY]?: ShippyFilter[];
} | null;

const convertDateFiltersToTimezone = (filters: ShippyFilter[], sellerTimeZone: string): ShippyFilter[] => {
  return filters
    .map((filter) => {
      if (filter.id !== FILTER_IDS.orderDate) {
        return filter;
      }

      const dateValue = filter.value as DateValue;
      if (!dateValue || typeof dateValue !== 'object') {
        return filter;
      }

      const convertedValue: DateValue = {};

      if (dateValue.from) {
        const fromMoment = convertDateToMomentDateObjAtMidnightInSellerTimeZone(dateValue.from, sellerTimeZone);
        convertedValue.from = fromMoment?.toISOString() ?? undefined;
      }

      if (dateValue.to) {
        const toMoment = convertDateToMomentDateObjAtMidnightInSellerTimeZone(dateValue.to, sellerTimeZone);
        // Set to end of day for the "to" date
        convertedValue.to = toMoment?.endOf('day').toISOString() ?? undefined;
      }

      // Only include the filter if at least one date was successfully converted
      if (!convertedValue.from && !convertedValue.to) {
        return null;
      }

      return {
        ...filter,
        value: convertedValue,
      };
    })
    .filter(Boolean) as ShippyFilter[];
};

/**
 * Hook to get Shippy search filters from route state.
 * Reads filters passed via React Router's location.state and converts date values to timezone-aware format.
 */
export const useShippySearchFilters = (): ShippyFilter[] | null => {
  const location = useLocation();
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);

  const state = location.state as LocationState;
  const shippyFilters = state?.[SHIPPY_FILTERS_STATE_KEY];
  const hasFilters = Array.isArray(shippyFilters) && shippyFilters.length > 0;
  // Serialize filter data so the memo only recomputes when actual values change,
  // not when location.state provides a new object reference on each navigation.
  const filtersKey = hasFilters ? JSON.stringify(shippyFilters) : null;

  return useMemo(() => {
    if (!filtersKey) {
      return null;
    }

    const parsedFilters: ShippyFilter[] = JSON.parse(filtersKey);

    // Convert date filters to timezone-aware format
    return convertDateFiltersToTimezone(parsedFilters, sellerTimeZone);
  }, [filtersKey, sellerTimeZone]);
};
